/* eslint-disable */
import { Button, Chip, makeStyles, Typography, Box } from '@material-ui/core';
import { Business, Edit, Add, CheckCircle } from '@material-ui/icons';
import cx from 'classnames';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    borderRadius: 12,
    border: '1px solid rgba(0, 168, 255, 0.1)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 3,
      background: 'linear-gradient(90deg, #5B65DC 0%, #6C7FDF 100%)',
      borderRadius: '12px 12px 0 0',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  accountIcon: {
    fontSize: 24,
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  statusText: {
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    flex: 1,
  },
  statusChip: {
    fontWeight: 600,
    '&.unlimited': {
      backgroundColor: '#E8F5E8',
      color: '#2E7D32',
    },
    '&.limited': {
      backgroundColor: '#FFF3E0',
      color: '#F57C00',
    },
  },
  accountsCounter: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  counterCard: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#f0f9ff',
    borderRadius: 8,
    border: '1px solid rgba(0, 168, 255, 0.2)',
    marginBottom: theme.spacing(1),
  },
  counterIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  },
  counterNumber: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
    lineHeight: 1,
  },
  counterLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  counterHint: {
    fontStyle: 'italic',
    fontSize: '0.75rem',
  },
  actionButton: {
    textTransform: 'none',
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 168, 255, 0.2)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 168, 255, 0.3)',
      transform: 'translateY(-1px)',
    },
    transition: 'all 0.3s ease',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    backgroundColor: 'rgba(244, 67, 54, 0.05)',
    borderRadius: 4,
    border: '1px solid rgba(244, 67, 54, 0.2)',
  },
}));

const AllowedAccountsSection = ({
                                  values,
                                  backendErrors,
                                  toggleAllowedAccountsModal,
                                }) => {
  const { en: { forms } } = useSelector((state) => state.i18n);
  const classes = useStyles();
  const countOfSelectedAccounts = values?.allowedAccounts?.length ?? 0;

  const isUnlimitedAccess = countOfSelectedAccounts === 0;
  const hasSelectedAccounts = countOfSelectedAccounts > 0;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Business className={classes.accountIcon} />
        <Typography className={classes.statusText}>
          {isUnlimitedAccess ? (
            'Access to all accounts'
          ) : (
            `Access to ${countOfSelectedAccounts} specific account${countOfSelectedAccounts === 1 ? '' : 's'}`
          )}
        </Typography>
        <Chip
          icon={<CheckCircle />}
          label={isUnlimitedAccess ? 'Unlimited' : 'Limited'}
          className={cx(classes.statusChip, {
            unlimited: isUnlimitedAccess,
            limited: !isUnlimitedAccess,
          })}
          size="small"
        />
      </div>

      {hasSelectedAccounts && (
        <Box className={classes.accountsCounter}>
          <Box className={classes.counterCard}>
            <Business className={classes.counterIcon} />
            <Box>
              <Typography className={classes.counterNumber}>
                {countOfSelectedAccounts}
              </Typography>
              <Typography className={classes.counterLabel}>
                {countOfSelectedAccounts === 1 ? 'Account Selected' : 'Accounts Selected'}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="textSecondary" className={classes.counterHint}>
            Click "Modify Access" to view and change selected accounts
          </Typography>
        </Box>
      )}

      {isUnlimitedAccess && (
        <Typography variant="body2" color="textSecondary" className={classes.emptyState}>
          This user will have access to all current and future accounts
        </Typography>
      )}

      <Box display="flex" justifyContent="flex-start" marginTop={2}>
        <Button
          variant="contained"
          color="primary"
          className={classes.actionButton}
          startIcon={hasSelectedAccounts ? <Edit /> : <Add />}
          disabled={!values}
          onClick={() => {
            toggleAllowedAccountsModal(true);
          }}
        >
          {hasSelectedAccounts ? 'Modify Access' : 'Set Account Access'}
        </Button>
      </Box>

      {(!!backendErrors['accountAccess'] || !!backendErrors['allowedAccounts']) && (
        <Typography className={classes.error}>
          {backendErrors['accountAccess'] || backendErrors['allowedAccounts']}
        </Typography>
      )}
    </div>
  );
};

export default AllowedAccountsSection;
